import './EditGroupMembership.css'
import { EnqueueSnackbar, enqueueSnackbar } from "notistack"
import { AdminContextInterface, UserGroupReference, adminCanEditSomeGroups, adminCanEditUserGroup, adminCanViewSomeGroups, getReferenceForGroup } from "../interfaces/ApplicationInterfaces"
import { useContext, useEffect, useState } from "react";
import { AdminContext } from "../contexts/AdminContext";
import { Box, Button, ButtonGroupButtonContext, Divider, IconButton, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { COSMOS_API, POST_ENDPOINT } from "../api/API";
import { GroupAdd, GroupRemove, Refresh } from "@mui/icons-material";


export interface EditGroupMembershipProps {
    objectId: string,
    country: string,
    userGroups: Array<UserGroupReference>,
    setUserGroupState: React.Dispatch<React.SetStateAction<Array<UserGroupReference>>>,
    enqueueSnackbar: EnqueueSnackbar
}

export interface EditGroupEntryProps {
    editedGroups: Array<UserGroupReference>,
    setEditedGroups: React.Dispatch<React.SetStateAction<Array<UserGroupReference>>>,
    groupId: string,
    groupName: string,
    inGroup: boolean,
    enabled: boolean,
    changed: boolean
}

const adminCanEditUserGroups = (adminCredentials: AdminContextInterface) => {
    return (adminCredentials.isOrganizationAdmin || adminCredentials.isSuperAdmin) && (adminCanEditSomeGroups(adminCredentials))
}

const EditGroupEntry = (props: EditGroupEntryProps) => {
    return (
        <>
            <TableCell sx={{width: '40%', borderLeft: (props.changed ? (props.inGroup ? "4px solid green" : "4px solid red") : "none")}}>
                <Typography>{props.groupName}</Typography>
            </TableCell>
            <TableCell>
                {props.changed && <Typography sx={{color: props.inGroup ? "green" : "red"}}>- {props.inGroup ? "Added" : "Removed"} -</Typography>}
            </TableCell>
            <TableCell sx={{textAlign: "right"}}>
                {props.inGroup ? (
                    <Button
                        onClick={() => props.setEditedGroups(
                            [...props.editedGroups.filter(editGroup => editGroup.id != props.groupId)]
                        )}
                        variant="contained"
                        color="primary"
                        size="medium"
                        endIcon={<GroupRemove/>}
                        disabled={!props.enabled}
                    >
                        Remove
                    </Button>
                    ) : (
                    <Button
                        onClick={() => props.setEditedGroups(
                            [...props.editedGroups, { id: props.groupId, name: props.groupName, customMetadata: {} }]
                        )}
                        variant="contained"
                        color="primary"
                        endIcon={<GroupAdd />}
                        size="medium"
                        disabled={!props.enabled}
                    >
                        Add
                </Button>
                    )
                }
            </TableCell>
        </>
    )
}

export const EditGroupMembership = (props: EditGroupMembershipProps) => {
    const { adminCredentials, getToken } = useContext(AdminContext);
    const [enableEditing, setEnableEditing] = useState<boolean>(true);
    const [editedGroups, setEditedGroups] = useState<Array<UserGroupReference>>([...props.userGroups])
    const [showLoading, setShowLoading] = useState<boolean>(false);

    useEffect(() => {
        console.log("setEditedGroups on userGroups update");
        console.log(`User groups: ${JSON.stringify(props.userGroups)}`);
        console.log(`Edited groups before: ${JSON.stringify(editedGroups)}`);
        setEditedGroups([...props.userGroups]);
    }, [props.userGroups]);

    const refreshUserGroupState = async(
        userId: string,
        country: string
    ) => {
        setEnableEditing(false);
        setShowLoading(true);
        let groupDataRequest = {
            objectId: userId,
            country: country,
            returnNested: true,
            includeGroups: true
        };
        const res = await COSMOS_API.POST(
            groupDataRequest,
            POST_ENDPOINT.GetUserDataAdmin,
            getToken()
        );
        if (res.ok) {
            props.setUserGroupState([...res.respBody.groups]);
        }
        setEnableEditing(true);
        setShowLoading(false);
    }

    const submitUserGroupUpdates = async(
        userId: string,
        addGroups: Array<string>,
        removeGroups: Array<string>
    ) => {
        setEnableEditing(false);
        setShowLoading(true);
        let updateRequestBody = {
            userId,
            addGroups: [...addGroups],
            removeGroups: [...removeGroups]
        };
        const res = await COSMOS_API.POST(
            updateRequestBody,
            POST_ENDPOINT.SaveUserGroupData,
            getToken()
        );
        if (res.ok) {
            props.setUserGroupState([...editedGroups]);
            enqueueSnackbar("User group memberships updated", { variant: 'success' });
            setEnableEditing(true);
        } else {
            let resultingGroups = [...editedGroups]
            if ((res.respBody?.failedToAdd || res.respBody?.failedToRemove) && (addGroups.length + removeGroups.length) > ((res.respBody?.failedToAdd?.length ?? 0) + (res.respBody?.failedToRemove?.length ?? 0))) {
                if (res.respBody?.failedToAdd) {
                    res.respBody.failedToAdd.foreach((failedId: string) => {
                        resultingGroups = resultingGroups.filter(groupRef => groupRef.id !== failedId);
                    });
                }
                if (res.respBody?.failedToRemove) {
                    res.respBody.failedToRemove.foreach((failedId: string) => {
                        if (!resultingGroups.some(resGroup => resGroup.id == failedId)) {
                            const stillPresentGroup = adminCredentials?.allGroups?.[failedId];
                            if (stillPresentGroup) {
                                resultingGroups.push(getReferenceForGroup(stillPresentGroup));
                            }
                        }
                    });
                }
                props.setUserGroupState([...resultingGroups]);
                enqueueSnackbar(`Some group memberships could not be updated.`, { variant: 'error' });
                setEnableEditing(true);
            } else {
                enqueueSnackbar(`Unable to update group memberships: ${res.respBody?.userMessage}`, { variant: 'error' });
                setEnableEditing(true);
            }
        }
        setShowLoading(false);
    }

    return (
        <>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                paddingLeft={2}
                paddingRight={2}
                paddingBottom={2}
            >
                <Typography variant="h6">User is a member of the following groups:</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    endIcon={<Refresh />}
                    onClick={() => refreshUserGroupState(props.objectId, props.country)} disabled={!enableEditing}>
                    <Typography>Refresh</Typography>
                </Button>
            </Box>
            {showLoading && <LinearProgress sx={{ width: '100%' }} />}
            <TableContainer
                component={Paper}
                className={'table-wrap in-page-apptable py-4'}
                style={{ "overflowY": "auto" }}
            >
                <Table className="groups-table">
                    <TableBody>
                        {((!editedGroups) || (editedGroups.length == 0)) && (
                            <TableRow key={`noGroups-ingrpedit`}>
                                <TableCell sx={{width: '40%'}}>
                                    <Typography color="gray">This user is not a member of any groups.</Typography>
                                </TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        )}
                        {editedGroups.map(group => {
                            return (
                                <TableRow key={`${group.id}-ingrpedit`}>
                                    <EditGroupEntry
                                        editedGroups={editedGroups}
                                        setEditedGroups={setEditedGroups}
                                        groupId={group.id}
                                        groupName={adminCredentials.allGroups[group.id].displayName}
                                        inGroup={true}
                                        enabled={enableEditing && adminCanEditUserGroup(adminCredentials, group.id)}
                                        changed={!props.userGroups.some(userGroup => userGroup.id == group.id)}
                                    />
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                paddingTop={4}
                paddingLeft={2}
                paddingRight={2}
                paddingBottom={2}
            >
                <Typography variant="h6">Other groups</Typography>
            </Box>
            <TableContainer
                component={Paper}
                className={'table-wrap in-page-apptable py-4'}
                style={{ "overflowY": "auto" }}
            >
                <Table className="groups-table">
                    <TableBody>
                        {Object.keys(adminCredentials.allGroups).filter(groupId => !editedGroups.some(editGroup => editGroup.id == groupId)).map(groupId => {
                            return (
                                <TableRow key={`${groupId}-ingrpedit`}>
                                        <EditGroupEntry
                                            editedGroups={editedGroups}
                                            setEditedGroups={setEditedGroups}
                                            groupId={groupId}
                                            groupName={adminCredentials.allGroups[groupId].displayName}
                                            inGroup={false}
                                            enabled={enableEditing && adminCanEditUserGroup(adminCredentials, groupId)}
                                            changed={props.userGroups.some(userGroup => userGroup.id == groupId)}
                                        />
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {adminCanEditUserGroups(adminCredentials) && (
                <Stack direction={"row"} spacing={4} paddingTop={4}>
                    <Button
                        onClick={() => {
                            submitUserGroupUpdates(
                                props.objectId,
                                editedGroups.filter(editGroup => !props.userGroups.some(userGroup => userGroup.id == editGroup.id)).map(group => group.id),
                                props.userGroups.filter(userGroup => !editedGroups.some(editGroup => editGroup.id == userGroup.id)).map(group => group.id)
                            );
                        }}
                        disabled={
                            (!enableEditing) || (
                                props.userGroups.every(userGroup => editedGroups.some(editGroup => editGroup.id == userGroup.id))
                                && editedGroups.every(editGroup => props.userGroups.some(userGroup => userGroup.id == editGroup.id))
                            )
                        }
                        variant="contained"
                        color="primary"
                    >
                        Save Changes
                    </Button>
                    <Button
                        onClick={() => { setEditedGroups([ ...props.userGroups ]); }}
                        disabled={
                            (!enableEditing) || (
                                props.userGroups.every(userGroup => editedGroups.some(editGroup => editGroup.id == userGroup.id))
                                && editedGroups.every(editGroup => props.userGroups.some(userGroup => userGroup.id == editGroup.id))
                            )
                        }
                        variant="contained"
                        color="primary"
                    >
                        Discard Changes
                    </Button>
                </Stack>
            )}
        </>
    )
}